import Phaser from 'phaser';
import { TextureKey } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import { COIN_EUR, type SpecialCoinId } from '../../../shared/scoring.js';
import type { WorldScale } from '../core/WorldScale.js';
import { CSS, PALETTE, UI_FONT, UI_HEAVY, formatEuro } from '../ui/style.js';
import type { CoinKind } from '../core/coinPlan.js';
import type { CourseSection } from './PipeField.js';

const C = TUNING.coins;
const B = TUNING.bonus;
const S = TUNING.special;
const F = TUNING.feedback;
const SPARK_KEY = 'coin-spark';
/** views of the coin spritesheets: front … edge (scripts/build-art-assets.mjs) */
const COIN_SPIN_FRAMES = 5;

/** where a coin was (pixels) */
export interface CoinSpot {
  x: number;
  y: number;
  /** a special coin */
  special?: SpecialCoinId;
}

const SPECIAL_TEXTURE: Record<SpecialCoinId, TextureKey> = { starter: TextureKey.CoinStarter, baby: TextureKey.CoinBaby };

interface Coin {
  img: Phaser.GameObjects.Image;
  kind: CoinKind;
  /** bonus coin: the subsidy it brings (€) and the checkpoint it reaches */
  eur: number;
  checkpoint?: number;
  /** special coin: which one */
  special?: SpecialCoinId;
}

/** an earned bonus coin waiting for a stretch that is not in view yet */
interface BonusOrder {
  eur: number;
  checkpoint?: number;
  onPlaced: (x: number) => void;
}

/** a stretch between two gates; its coins follow the line from one gap to the next, shifted by `offset` */
interface Stretch extends CourseSection {
  offset: number;
}

/**
 * The coins in the course: gold coins (own contribution), bonus coins (Grundzulage) and the special coins.
 * Gold and special coins come by plan (core/coinPlan.ts, TUNING.special) just before they come into view; bonus
 * coins are earned and appear in the next stretch that is not in view yet. All lie on the line from one gap to
 * the next, shifted up or down a little (own seeded RNG → reproducible per run). Gold and special coins are
 * collected by flying through them; bonus coins are pulled to the figure (`pullBonus`).
 */
export class CoinField {
  private readonly coins: Coin[] = [];
  private readonly stretches: Stretch[] = [];
  private readonly orders: BonusOrder[] = [];
  /** "+50 €" / "−50 €" labels, reused (creating a Text renders a new texture every time) */
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private readonly rng = new Phaser.Math.RandomDataGenerator();
  private spinPhase = 0;
  /** view of the turning coin shown now (all coins turn in step) */
  private spinFrame = 0;
  private spinFlip = false;
  /** the special coins pulse (s) */
  private pulseSec = 0;
  /** pixel sparks when a coin is collected (reused) */
  private readonly sparkPool: Phaser.GameObjects.Image[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly ws: WorldScale,
    /** y (pixels) of the ground surface */
    private readonly floorY: number,
    /** no coins above this line (world units from the top) */
    private readonly minY: number,
    private readonly depth: number,
    /** right edge of the view (pixels) */
    private readonly viewWidth: number,
    /**
     * sparks of a collected coin: above the figure; its amount one above (the newest on top), the special coins'
     * two above – always readable
     */
    private readonly fxDepth: number = depth + 1,
  ) {}

  /** Remove all coins and prepare a new sequence. */
  reset(seed: string): void {
    this.spinPhase = 0;
    this.spinFrame = 0;
    this.spinFlip = false;
    this.pulseSec = 0;
    for (const c of this.coins) this.free(c);
    this.stretches.length = 0;
    this.orders.length = 0;
    for (const t of this.labels) {
      this.scene.tweens.killTweensOf(t);
      t.setActive(false).setVisible(false);
    }
    this.rng.sow([`${seed}:coins`]);
  }

  /** A new stretch between two gates (announced before it scrolls into view). */
  addStretch(s: CourseSection): void {
    this.stretches.push({ ...s, offset: this.ws.px(this.rng.realInRange(-C.maxOffset, C.maxOffset)) });
    // a new stretch is announced before it comes into view: earned bonus coins waiting for one go there
    while (this.orders.length && this.placeBonus(this.orders[0])) this.orders.shift();
  }

  /**
   * Put a gold coin into the course at `x` (pixels) – or a little later: never inside a gate and not closer
   * to another coin than `minDistance`.
   */
  place(x: number): void {
    this.putInCourse('gold', x);
  }

  /** Put a special coin into the course at `x` (pixels) – placed like a gold coin. */
  placeSpecial(x: number, id: SpecialCoinId): void {
    this.putInCourse('special', x, id);
  }

  private putInCourse(kind: 'gold' | 'special', x: number, special?: SpecialCoinId): void {
    const margin = this.ws.px(C.displaySize) * 0.75;
    x = this.clear(x);
    const s = this.stretches.find((q) => x <= q.x1 - margin);
    if (s) x = this.clear(Math.max(x, s.x0 + margin));
    this.put(kind, x, s, 0, undefined, special);
  }

  /**
   * An earned bonus coin: it appears in the next stretch that is not in view yet (waits for one if
   * needed). `onPlaced` gets its position (pixels).
   */
  spawnBonus(eur: number, checkpoint: number | undefined, onPlaced: (x: number) => void): void {
    const order = { eur, checkpoint, onPlaced };
    if (this.orders.length || !this.placeBonus(order)) this.orders.push(order);
  }

  /** Place a bonus coin in the middle of the first stretch out of view – false if there is none with room. */
  private placeBonus(o: BonusOrder): boolean {
    const margin = this.ws.px(C.displaySize) * 0.75;
    for (const s of this.stretches) {
      if (s.x0 <= this.viewWidth) continue;
      const x = this.clear((s.x0 + s.x1) / 2);
      if (x > s.x1 - margin) continue;
      this.put('bonus', x, s, o.eur, o.checkpoint);
      o.onPlaced(x);
      return true;
    }
    return false;
  }

  /** `x` or further right, at least `minDistance` away from every coin in the course */
  private clear(x: number): number {
    const d = this.ws.px(C.minDistance);
    for (let i = 0; i < 20; i++) {
      const c = this.coins.find((q) => q.img.active && Math.abs(q.img.x - x) < d);
      if (!c) break;
      x = c.img.x + d;
    }
    return x;
  }

  private put(kind: CoinKind, x: number, s: Stretch | undefined, eur: number, checkpoint?: number, special?: SpecialCoinId): void {
    const size = this.ws.px(C.displaySize) * (kind === 'bonus' ? B.scale : kind === 'special' ? S.scale : 1);
    let y = this.floorY / 2;
    if (s) {
      const t = Phaser.Math.Clamp((x - s.x0) / Math.max(1, s.x1 - s.x0), 0, 1);
      y = s.y0 + (s.y1 - s.y0) * t + s.offset;
    }
    const r = Math.max(size, this.ws.px(C.displaySize)) / 2;
    const top = this.ws.px(Math.max(TUNING.pipes.margin, this.minY, kind === 'special' ? S.minY : 0)) + r;
    const bottom = this.floorY - this.ws.px(TUNING.pipes.margin) - r;
    const coin = this.obtain(kind, special);
    coin.eur = eur;
    coin.checkpoint = checkpoint;
    coin.img.setPosition(x, Phaser.Math.Clamp(y, top, bottom)).setDisplaySize(size, size);
  }

  /** Scroll by `dx` pixels, recycle coins and stretches that left the screen. */
  scroll(dx: number, dt: number): void {
    const r = this.ws.px(C.displaySize);
    this.spinPhase = (this.spinPhase + Phaser.Math.DegToRad(C.rotationSpeed) * dt) % (Math.PI * 2);
    // The coin turns in depth: front → edge → front over half a turn, its edge (thickness) on the left;
    // the second half mirrored (edge on the right). The circular pick-up area stays unchanged.
    const half = this.spinPhase % Math.PI;
    const toEdge = Math.min(half, Math.PI - half) / (Math.PI / 2);
    this.spinFrame = Math.round(toEdge * (COIN_SPIN_FRAMES - 1));
    this.spinFlip = this.spinPhase >= Math.PI;
    this.pulseSec += dt;
    const pulse = 1 + S.pulse * Math.sin((this.pulseSec / S.pulseSec) * Math.PI * 2);
    for (const c of this.coins) {
      if (!c.img.active) continue;
      c.img.x -= dx;
      if (c.kind === 'special') {
        const size = this.ws.px(C.displaySize) * S.scale * pulse;
        c.img.setDisplaySize(size, size);
      } else c.img.setFrame(this.spinFrame, false, false).setFlipX(this.spinFlip);
      if (c.img.x + r < 0) this.free(c);
    }
    for (const s of this.stretches) {
      s.x0 -= dx;
      s.x1 -= dx;
    }
    while (this.stretches.length && this.stretches[0].x1 < 0) this.stretches.shift();
  }

  /** Collect all gold and special coins touching the circle (pixels). Returns where they were. */
  collect(cx: number, cy: number, radius: number): CoinSpot[] {
    const got: CoinSpot[] = [];
    for (const c of this.coins) {
      if (!c.img.active || c.kind === 'bonus') continue;
      // the larger special coins are as easy to hit as they look
      const reach = radius + this.ws.px(C.hitRadius) * (c.kind === 'special' ? S.scale : 1);
      const dx = c.img.x - cx;
      const dy = c.img.y - cy;
      if (dx * dx + dy * dy > reach * reach) continue;
      got.push({ x: c.img.x, y: c.img.y, special: c.special });
      this.free(c);
      this.pop(c);
    }
    return got;
  }

  /**
   * Bonus coins closer to the figure (at `x`, pixels) than `magnet` (pixels) are pulled to it, after the
   * gold coins in front of them. `take` starts the flight.
   */
  pullBonus(x: number, radius: number, magnet: number, take: (x: number, y: number, eur: number, checkpoint?: number) => void): void {
    const reach = radius + this.ws.px(C.hitRadius);
    for (const c of this.coins) {
      if (!c.img.active || c.kind !== 'bonus' || c.img.x - x > magnet) continue;
      if (this.coins.some((g) => g.img.active && g.kind === 'gold' && g.img.x < c.img.x && g.img.x > x - reach)) continue;
      this.free(c);
      take(c.img.x, c.img.y, c.eur, c.checkpoint);
    }
  }

  /** bonus coins in the course or still waiting for a stretch: what they bring (€) */
  get bonusEur(): number {
    let eur = this.orders.reduce((sum, o) => sum + o.eur, 0);
    for (const c of this.coins) if (c.img.active && c.kind === 'bonus') eur += c.eur;
    return eur;
  }

  /** x (pixels) of the last coin still to come ahead of `x` – null if there is none */
  lastAhead(x: number): number | null {
    let last: number | null = null;
    for (const c of this.coins) if (c.img.active && c.img.x > x && (last === null || c.img.x > last)) last = c.img.x;
    return last;
  }

  /** gold coins in the course that can still be collected (ahead of `x`) */
  goldAhead(x: number): number {
    return this.coins.filter((c) => c.img.active && c.kind === 'gold' && c.img.x > x).length;
  }

  /** The next gold or special coin ahead of `x` (autopilot of the demo). */
  nextAhead(x: number): CoinSpot | null {
    let best: Phaser.GameObjects.Image | null = null;
    for (const c of this.coins) if (c.img.active && c.kind !== 'bonus' && c.img.x > x && (!best || c.img.x < best.x)) best = c.img;
    return best ? { x: best.x, y: best.y } : null;
  }

  /** A hit costs a coin: it drops out of the figure, "−50 €" floats up. */
  lose(x: number, y: number, subsidyEur = 0): void {
    this.float(`−${formatEuro(COIN_EUR)}`, CSS.lost, x, y - this.ws.px(40));
    // the subsidy lost with it, below in the subsidy's colour
    if (subsidyEur > 0) this.float(`−${formatEuro(subsidyEur)}`, CSS.bonus, x, y + this.ws.px(30));
    const fx = this.scene.add
      .image(x, y, TextureKey.Coin)
      .setDisplaySize(this.ws.px(C.displaySize), this.ws.px(C.displaySize))
      .setDepth(this.depth);
    this.scene.tweens.add({
      targets: fx,
      x: x - this.ws.px(60),
      y: y + this.ws.px(220),
      angle: -200,
      alpha: 0,
      duration: 700,
      ease: 'Quad.In',
      onComplete: () => fx.destroy(),
    });
  }

  /**
   * A text that pops up, floats up and fades out (reused objects). `size` in WU. The newest text lies on top of the
   * ones still in view – `special` texts (the special coins) above all others, a later amount never covers them.
   */
  float(text: string, color: string, x: number, y: number, size: number = F.labelSize, special = false): void {
    const label = this.label(text, color, size).setAlpha(1).setScale(0.4).setDepth(this.fxDepth + (special ? 2 : 1));
    this.scene.children.bringToTop(label);
    // a long label (special coins) stays inside the view – the figure flies close to the left edge
    const half = label.width / 2 + this.ws.px(10);
    // … and below the HUD: it rises no higher than the line no coin goes above (rising less if needed)
    const end = Math.max(y - this.ws.px(special ? F.specialRise : F.rise), this.ws.px(this.minY) * 0.8 + label.height / 2);
    label.setPosition(Phaser.Math.Clamp(x, half, Math.max(half, this.viewWidth - half)), Math.max(y, end));
    const ms = (special ? F.specialSec : F.sec) * 1000;
    const hold = special ? F.specialHoldShare : F.holdShare;
    this.scene.tweens.add({ targets: label, scale: 1, duration: 220, ease: 'Back.Out' });
    this.scene.tweens.add({ targets: label, y: end, duration: ms, ease: 'Quad.Out' });
    this.scene.tweens.add({
      targets: label,
      alpha: 0,
      delay: ms * hold,
      duration: ms * (1 - hold),
      onComplete: () => label.setActive(false).setVisible(false),
    });
  }

  /**
   * Pixel sparks fly out of (x, y) in `tint` – more for a special coin. Pooled images of a white pixel, drawn like
   * the coins.
   */
  sparks(x: number, y: number, tint: number, big = false): void {
    const n = big ? F.specialSparks : F.sparks;
    const size = Math.max(2, Math.round(this.ws.px(F.sparkSize)));
    for (let i = 0; i < n; i++) {
      const p = this.spark().setPosition(x, y).setDisplaySize(size, size).setTint(i % 3 ? tint : 0xffffff).setAlpha(1);
      const angle = (i / n) * Math.PI * 2 + Phaser.Math.FloatBetween(-0.3, 0.3);
      const dist = this.ws.px(F.sparkSpeed) * Phaser.Math.FloatBetween(0.25, 0.55) * (big ? 1.3 : 1);
      this.scene.tweens.add({
        targets: p,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: p.scaleX * 0.3,
        scaleY: p.scaleY * 0.3,
        duration: Phaser.Math.Between(380, 620),
        ease: 'Quad.Out',
        onComplete: () => p.setActive(false).setVisible(false),
      });
    }
  }

  private spark(): Phaser.GameObjects.Image {
    if (!this.scene.textures.exists(SPARK_KEY)) {
      const t = this.scene.textures.createCanvas(SPARK_KEY, 2, 2);
      const ctx = t?.getContext();
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 2, 2);
        t!.refresh();
      }
    }
    let p = this.sparkPool.find((q) => !q.active);
    if (!p) {
      p = this.scene.add.image(0, 0, SPARK_KEY).setDepth(this.fxDepth);
      this.sparkPool.push(p);
    }
    this.scene.tweens.killTweensOf(p);
    return p.setActive(true).setVisible(true);
  }

  /** Feedback: a copy of the coin flies up and fades out, the value of a gold coin pops up above it with sparks. */
  private pop(c: Coin): void {
    const { x, y } = c.img;
    if (c.kind === 'gold') {
      this.float(`+${formatEuro(COIN_EUR)}`, CSS.gold, x, y - this.ws.px(50));
      this.sparks(x, y, PALETTE.yellow);
    }
    const fx = this.scene.add
      .image(x, y, c.special ? SPECIAL_TEXTURE[c.special] : TextureKey.CoinSpin, c.special ? undefined : this.spinFrame)
      .setDisplaySize(c.img.displayWidth, c.img.displayHeight)
      .setFlipX(!c.special && this.spinFlip)
      .setDepth(this.depth);
    this.scene.tweens.add({
      targets: fx,
      y: y - this.ws.px(70),
      alpha: 0,
      scaleX: fx.scaleX * 1.4,
      scaleY: fx.scaleY * 1.4,
      duration: 260,
      onComplete: () => fx.destroy(),
    });
  }

  private label(text: string, color: string, size: number): Phaser.GameObjects.Text {
    const px = (wu: number) => Math.round(this.ws.px(wu));
    const fontSize = `${px(size)}px`;
    const free = this.labels.filter((q) => !q.active);
    // a free label with the same text and size needs no new texture
    let t = free.find((q) => q.text === text && q.style.fontSize === fontSize) ?? free[0];
    if (!t) {
      t = this.scene.add
        .text(0, 0, text, {
          fontFamily: UI_FONT,
          fontSize,
          fontStyle: UI_HEAVY,
          color: CSS.text,
          stroke: CSS.ink,
          strokeThickness: px(size * 0.24),
          align: 'center',
        })
        .setOrigin(0.5);
      this.labels.push(t);
    }
    if (t.style.fontSize !== fontSize) t.setFontSize(fontSize).setStroke(CSS.ink, px(size * 0.24));
    if (t.text !== text) t.setText(text);
    t.setColor(color);
    this.scene.tweens.killTweensOf(t);
    return t.setActive(true).setVisible(true);
  }

  private obtain(kind: CoinKind, special?: SpecialCoinId): Coin {
    let c = this.coins.find((q) => !q.img.active);
    if (!c) {
      c = { img: this.scene.add.image(0, 0, TextureKey.CoinSpin, 0).setDepth(this.depth), kind, eur: 0 };
      this.coins.push(c);
    }
    c.kind = kind;
    c.special = special;
    this.scene.tweens.killTweensOf(c.img);
    if (special) c.img.setTexture(SPECIAL_TEXTURE[special]);
    else c.img.setTexture(kind === 'gold' ? TextureKey.CoinSpin : TextureKey.CoinBonusSpin, this.spinFrame);
    c.img
      .setAngle(0)
      .setAlpha(1)
      .setFlipX(!special && this.spinFlip)
      .setActive(true)
      .setVisible(true);
    return c;
  }

  private free(c: Coin): void {
    this.scene.tweens.killTweensOf(c.img);
    c.img.setActive(false).setVisible(false);
  }
}
